import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import {
  clearExpensesTrackingData,
  clearExpenseTrackingForDateChange,
} from 'scenes/ExpenseTracking/store/actions/expenses-tracking.actions';
import * as actions from './actions/invoices.actions';
import {
  removeLastInvoiceError,
  updateInvoicesFromGraphqlMutate,
} from './actions/invoices.actions';
import { createPurchaseInvoice } from './thunks/createPurchaseInvoice';
import { deleteInvoiceSortRule } from './thunks/deleteInvoiceSortRule';
import { loadInvoiceSortRules } from './thunks/loadInvoiceSortRules';
import { loadProjectInvoices } from './thunks/loadProjectInvoices';
import { loadProjectInvoicesByRows } from './thunks/loadProjectInvoicesByRows';
import { loadProjectSaleInvoices } from './thunks/loadProjectSaleInvoices';
import { patchApproveRejectInvoice } from './thunks/patchApproveRejectInvoice';
import { patchCreateInvoiceSortRule } from './thunks/patchCreateInvoiceSortRule';
import { patchSortMultipleInvoicesBySortRule } from './thunks/patchSortMultipleInvoicesBySortRule';
import { patchUpdateInvoice } from './thunks/patchUpdateInvoice';
import { patchUpdateMultipleInvoices } from './thunks/patchUpdateMultipleInvoices';
import { loadInvoiceScanningAddress } from './thunks/loadInvoiceScanningAddress';
import { activateInvoiceScanningAddressThunk } from './thunks/activateInvoiceScanningAddress.thunk';
import { deactivateInvoiceScanningAddressThunk } from './thunks/deactivateInvoiceScanningAddress.thunk';
import { loadPurchaseInvoicePdfUrls } from './thunks/loadPurchaseInvoicePdfUrls.thunk';

const invoicesAdapter = createEntityAdapter();
const saleInvoicesAdapter = createEntityAdapter();

const invoiceSortingRulesAdapter = createEntityAdapter();
const invoiceSortRulesAdapter = createEntityAdapter();

const resetState = {
  invoices: invoicesAdapter.getInitialState(),
  invoiceRows: {},
  saleInvoices: saleInvoicesAdapter.getInitialState(),
  invoiceSortingRules: invoiceSortingRulesAdapter.getInitialState(),
  invoiceSortRules: invoiceSortRulesAdapter.getInitialState(),
  loadingInvoiceSortingRules: false,
  loadingInvoiceSortRules: false,
  isInvoicesLoaded: false,
  selectedInvoice: '',
  loading: false,
  error: null,
  invoiceUpdateError: null,
  invoiceSortingRuleError: null,
  invoiceApprovalError: null,
  invoiceErrors: [],
  invoiceScanningAddress: null,
  pdfUrlsByInvoiceId: {},
};

export const invoicesSlice = createSlice({
  name: 'invoices',
  initialState: {
    invoices: invoicesAdapter.getInitialState(),
    invoiceRows: {},
    saleInvoices: saleInvoicesAdapter.getInitialState(),
    invoiceSortingRules: invoiceSortingRulesAdapter.getInitialState(),
    invoiceSortRules: invoiceSortRulesAdapter.getInitialState(),
    loadingInvoiceSortingRules: false,
    loadingInvoiceSortRules: false,
    isInvoicesLoaded: false,
    selectedInvoice: '',
    loading: false,
    error: null,
    invoiceUpdateError: null,
    invoiceSortingRuleError: null,
    invoiceApprovalError: null,
    invoiceErrors: [],
    invoiceScanningAddress: null,
    // invoiceId -> { urls, fetchedAt, loading }: signed links to its PDFs.
    pdfUrlsByInvoiceId: {},
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadPurchaseInvoicePdfUrls.pending, (state, action) => {
        const { invoiceId } = action.meta.arg;
        state.pdfUrlsByInvoiceId[invoiceId] = {
          ...state.pdfUrlsByInvoiceId[invoiceId],
          loading: true,
        };
      })
      .addCase(loadPurchaseInvoicePdfUrls.fulfilled, (state, action) => {
        state.pdfUrlsByInvoiceId[action.meta.arg.invoiceId] = {
          ...action.payload,
          loading: false,
        };
      })
      .addCase(loadPurchaseInvoicePdfUrls.rejected, (state, action) => {
        // No fetchedAt, so the next dialog to open tries again.
        state.pdfUrlsByInvoiceId[action.meta.arg.invoiceId] = {
          urls: [],
          loading: false,
        };
      })
      .addCase(loadProjectInvoices.pending, (state) => {
        state.loading = true;
        state.isInvoicesLoaded = false;
      })
      .addCase(loadProjectInvoices.fulfilled, (state, action) => {
        invoicesAdapter.setMany(state.invoices, action.payload);
        state.loading = false;
        state.isInvoicesLoaded = true;
      })
      .addCase(loadProjectInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.isInvoicesLoaded = false;
      })
      .addCase(loadProjectInvoicesByRows.pending, (state) => {
        state.loading = true;
        state.isInvoicesLoaded = false;
      })
      .addCase(loadProjectInvoicesByRows.fulfilled, (state, action) => {
        invoicesAdapter.setMany(state.invoices, action.payload.invoices);

        action.payload.invoiceRows.forEach((row) => {
          const { invoice } = row;

          if (!state.invoiceRows[invoice]) {
            state.invoiceRows[invoice] = [];
          }

          state.invoiceRows[invoice].push(row);
        });

        // Update the loading state
        state.loading = false;
        state.isInvoicesLoaded = true;
      })
      .addCase(loadProjectInvoicesByRows.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.isInvoicesLoaded = false;
      })
      .addCase(loadProjectSaleInvoices.pending, (state) => {
        state.loading = true;
        state.isInvoicesLoaded = false;
      })
      .addCase(loadProjectSaleInvoices.fulfilled, (state, action) => {
        saleInvoicesAdapter.setAll(state.saleInvoices, action.payload);
        // state.loading = false;
        // state.isInvoicesLoaded = true;
      })
      .addCase(loadProjectSaleInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.isInvoicesLoaded = false;
      })
      .addCase(clearExpensesTrackingData, (state) => {
        return resetState;
      })
      .addCase(clearExpenseTrackingForDateChange, (state) => {
        state.invoices = invoicesAdapter.getInitialState();
        state.saleInvoices = saleInvoicesAdapter.getInitialState();
      })
      .addCase(updateInvoicesFromGraphqlMutate, (state, action) => {
        console.log('updateInvoicesFromGraphqlMutate', action);
        if (action.payload.length) {
          invoicesAdapter.upsertMany(state.invoices, action.payload);
        } else {
          invoicesAdapter.upsertOne(state.invoices, action.payload);
        }
      })
      .addCase(actions.setInvoiceLoadedAmount, (state, action) => {
        state.invoiceLoadedAmount = action.payload;
      })
      .addCase(actions.setSaleInvoiceLoadedAmount, (state, action) => {
        state.saleInvoiceLoadedAmount = action.payload;
      })
      // Put a freshly created invoice into the store directly. Nothing else
      // refetches after the dialog closes, and the refetch that eventually does
      // run is bounded by the view's date range — so without this an invoice
      // saved from the dashboard camera stayed invisible until a reload.
      .addCase(createPurchaseInvoice.fulfilled, (state, action) => {
        if (action.payload?.id) {
          // `accounting` rides along on the response to tell the dialog whether
          // the invoice also reached the accounting system. It is not part of
          // the stored row, so keep it out of the entity — otherwise it lingers
          // until a refetch replaces the entity with the real one.
          const { accounting, ...invoice } = action.payload;
          invoicesAdapter.upsertOne(state.invoices, invoice);
        }
      })
      .addCase(patchApproveRejectInvoice.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(patchApproveRejectInvoice.fulfilled, (state, action) => {
        console.log('RETURN PAYLOAD IN SLICE: ', action.payload);
        if (action.payload.length) {
          invoicesAdapter.upsertMany(state.invoices, action.payload);
        } else {
          invoicesAdapter.upsertOne(state.invoices, action.payload);
        }
        state.loading = false;
        if (action.payload?.alreadyApproved) {
          enqueueSnackbar('Lasku oli jo hyväksytty', {
            variant: 'info',
          });
        } else {
          enqueueSnackbar('Lasku hyväksytty/hylätty', {
            variant: 'success',
          });
        }
      })
      .addCase(patchApproveRejectInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.invoiceApprovalError = action.error;
        state.invoiceErrors.push(action.error);
        enqueueSnackbar('Virhe laskun hyväksynnässä/hylkäyksessä', {
          variant: 'error',
        });
      })

      .addCase(patchUpdateInvoice.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(patchUpdateInvoice.fulfilled, (state, action) => {
        console.log('RETURN PAYLOAD IN SLICE: ', action.payload);
        if (action.payload.length) {
          invoicesAdapter.setMany(state.invoices, action.payload);
        } else {
          invoicesAdapter.setOne(state.invoices, action.payload);
        }
        state.loading = false;
        enqueueSnackbar('Lasku tallennettu', {
          variant: 'success',
        });
      })
      .addCase(patchUpdateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.invoiceUpdateError = action.error;
        state.invoiceErrors.push(action.error);
        enqueueSnackbar('Virhe tallennettaessa laskua', {
          variant: 'error',
        });
      })
      .addCase(patchUpdateMultipleInvoices.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(patchUpdateMultipleInvoices.fulfilled, (state, action) => {
        console.log('RETURN PAYLOAD IN SLICE: ', action.payload);
        if (action.payload.length) {
          invoicesAdapter.upsertMany(state.invoices, action.payload);
        } else {
          invoicesAdapter.upsertOne(state.invoices, action.payload);
        }
        state.loading = false;
        enqueueSnackbar('Laskut tallennettu', {
          variant: 'success',
        });
      })
      .addCase(patchUpdateMultipleInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.invoiceUpdateError = action.error;
        state.invoiceErrors.push(action.error);
        enqueueSnackbar('Virhe tallennettaessa laskuja', {
          variant: 'error',
        });
      })
      .addCase(removeLastInvoiceError, (state) => {
        state.invoiceErrors.shift();
        console.log('ERROR ARRAY', state.invoiceErrors);
      })

      .addCase(loadInvoiceSortRules.pending, (state) => {
        state.loadingInvoiceSortRules = true;
      })
      .addCase(loadInvoiceSortRules.fulfilled, (state, action) => {
        invoiceSortRulesAdapter.setAll(state.invoiceSortRules, action.payload);

        state.loadingInvoiceSortingRules = false;
      })
      .addCase(loadInvoiceSortRules.rejected, (state, action) => {
        state.loadingInvoiceSortRules = false;
      })
      .addCase(patchCreateInvoiceSortRule.pending, (state) => {
        state.loadingInvoiceSortRules = true;
      })
      .addCase(patchCreateInvoiceSortRule.fulfilled, (state, action) => {
        invoiceSortRulesAdapter.upsertOne(
          state.invoiceSortRules,
          action.payload
        );
        state.loadingInvoiceSortRules = false;
        enqueueSnackbar('Lajittelusääntö tallennettu', {
          variant: 'success',
        });
      })
      .addCase(patchCreateInvoiceSortRule.rejected, (state, action) => {
        state.loadingInvoiceSortRules = false;
        state.error = action.error;
        enqueueSnackbar('Virhe tallennettaessa lajittelusääntöjä', {
          variant: 'error',
        });
      })
      .addCase(patchSortMultipleInvoicesBySortRule.pending, (state) => {})
      .addCase(
        patchSortMultipleInvoicesBySortRule.fulfilled,
        (state, action) => {
          console.log('action.payload', action.payload);
          invoicesAdapter.setMany(state.invoices, action.payload);

          enqueueSnackbar('Säännönmukaiset laskut lajiteltu', {
            variant: 'success',
          });
        }
      )
      .addCase(
        patchSortMultipleInvoicesBySortRule.rejected,
        (state, action) => {
          state.error = action.error;
          enqueueSnackbar('Virhe säännönmukaisten laskujen lajittelussa', {
            variant: 'error',
          });
        }
      )
      .addCase(deleteInvoiceSortRule.pending, (state) => {
        state.loadingInvoiceSortRules = true;
      })
      .addCase(deleteInvoiceSortRule.fulfilled, (state, action) => {
        console.log('action.payload', action.payload);
        invoiceSortRulesAdapter.removeOne(
          state.invoiceSortRules,
          action.payload.id
        );
        state.loadingInvoiceSortRules = false;
        enqueueSnackbar('Lajittelusääntö poistettu', {
          variant: 'success',
        });
      })
      .addCase(deleteInvoiceSortRule.rejected, (state, action) => {
        state.loadingInvoiceSortRules = false;
        state.error = action.error;
        enqueueSnackbar('Virhe poistettaessa lajittelusääntöä', {
          variant: 'error',
        });
      })
      .addCase(loadInvoiceScanningAddress.fulfilled, (state, action) => {
        const businessId = action.meta.arg.businessId;
        if (businessId) {
          state.invoiceScanningAddress = action.payload;
        }
      })
      .addCase(activateInvoiceScanningAddressThunk.pending, (state, action) => {
        if (
          state.invoiceScanningAddress &&
          state.invoiceScanningAddress.email === action.meta.arg.email
        ) {
          state.invoiceScanningAddress = {
            ...state.invoiceScanningAddress,
            active: true,
          };
        }
      })
      .addCase(
        deactivateInvoiceScanningAddressThunk.pending,
        (state, action) => {
          if (
            state.invoiceScanningAddress &&
            state.invoiceScanningAddress.email === action.meta.arg.email
          ) {
            state.invoiceScanningAddress = {
              ...state.invoiceScanningAddress,
              active: false,
              confirmationLink: null,
            };
          }
        }
      );
  },
});
